import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Leaf, Zap, Droplets, ArrowRight, Loader2 } from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { useGetSites } from "hooks/site/useGetSites";
import {
  useGetEnergieCarboneEmpreinte,
  useGetElevageCarboneEmpreinte,
  useGetSolCarboneEmpreinte,
  useGetGazFrigoCarboneEmpreinte,
  useGetTransportCarboneEmpreinte,
  useGetAchatCarboneEmpreinte,
  useGetImmobilisationCarboneEmpreinte,
  useGetDechetsCarboneEmpreinte,
  sumSummary,
} from "hooks/analytics/useGetCarboneEmpreinte";
import { useGetEnergieKpis, useGetEauKpis } from "hooks/analytics/useGetKpisAnalytics";
import type { User as BackendUser } from "types/user";
import "./DashboardOverviewPage.css";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function formatCO2(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
  return val.toFixed(1);
}

function formatNum(val: number, decimals = 2): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
  return val.toFixed(decimals);
}

const POSTE_COLORS = [
  "#ef4444", "#f97316", "#84cc16", "#06b6d4",
  "#3b82f6", "#8b5cf6", "#ec4899", "#78716c",
];
const POSTE_LABELS = [
  "Énergie", "Élevage", "Sols", "Gaz Réfrig.",
  "Transport", "Achats", "Immobilis.", "Déchets",
];
const ENERGIE_BAR_COLORS = ["#f59e0b", "#3b82f6", "#ef4444", "#8b5cf6", "#6b7280"];
const EAU_BAR_COLORS = ["#3b82f6", "#06b6d4", "#0ea5e9", "#60a5fa", "#93c5fd"];

type Props = { user: BackendUser };

const DashboardOverviewPage = ({ user }: Props) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  // Campaign = Oct→Sep fiscal year, e.g. "25/26"
  const currentCampaign =
    currentMonth >= 10
      ? `${currentYear % 100}/${(currentYear + 1) % 100}`
      : `${(currentYear - 1) % 100}/${currentYear % 100}`;

  const [selectedCampaign, setSelectedCampaign] = useState(currentCampaign);

  // Convert "25/26" → { from_period: "2025-10", to_period: "2026-09" }
  const campaignToRange = (c: string) => {
    const [s, e] = c.split("/").map((x) => 2000 + parseInt(x, 10));
    return { from_period: `${s}-10`, to_period: `${e}-09` };
  };

  const { from_period, to_period } = campaignToRange(selectedCampaign);
  const filters = { from_period, to_period };

  const { data: sites = [] } = useGetSites();

  // ── Carbon hooks (all 8 postes) ───────────────────────────────────
  const ec = useGetEnergieCarboneEmpreinte(filters);
  const elev = useGetElevageCarboneEmpreinte(filters);
  const sol = useGetSolCarboneEmpreinte(filters);
  const gaz = useGetGazFrigoCarboneEmpreinte(filters);
  const trans = useGetTransportCarboneEmpreinte(filters);
  const achat = useGetAchatCarboneEmpreinte(filters);
  const immob = useGetImmobilisationCarboneEmpreinte(filters);
  const dech = useGetDechetsCarboneEmpreinte(filters);

  // ── Energie / Eau KPI hooks ───────────────────────────────────────
  const energieKpis = useGetEnergieKpis(filters);
  const eauKpis = useGetEauKpis({ ...filters, dimension_type: "famille" });

  // ── Derived totals ─────────────────────────────────────────────────
  const posteTotals = useMemo(
    () => [
      sumSummary(ec.data?.summary),
      sumSummary(elev.data?.summary),
      sumSummary(sol.data?.summary),
      sumSummary(gaz.data?.summary),
      sumSummary(trans.data?.summary),
      sumSummary(achat.data?.summary),
      sumSummary(immob.data?.summary),
      sumSummary(dech.data?.summary),
    ],
    [ec.data, elev.data, sol.data, gaz.data, trans.data, achat.data, immob.data, dech.data]
  );

  const totalCO2 = useMemo(
    () => posteTotals.reduce((s, v) => s + v, 0),
    [posteTotals]
  );
  const energieTEP = useMemo(
    () => (energieKpis.data?.summary ?? []).reduce((s, i) => s + i.total, 0),
    [energieKpis.data]
  );
  const eauTotal = useMemo(
    () => (eauKpis.data?.summary ?? []).reduce((s, i) => s + i.total, 0),
    [eauKpis.data]
  );

  const carbonLoading = ec.isLoading || elev.isLoading || trans.isLoading;

  // ── Donut chart data ───────────────────────────────────────────────
  const donutData = useMemo(
    () => ({
      labels: POSTE_LABELS,
      datasets: [
        {
          data: posteTotals,
          backgroundColor: POSTE_COLORS,
          borderWidth: 2,
          borderColor: "#fff",
        },
      ],
    }),
    [posteTotals]
  );

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${ctx.label}: ${formatCO2(ctx.raw)} kg eq.CO₂`,
        },
      },
    },
  };

  const availableCampaigns = useMemo(() => {
    const list: string[] = [];
    for (let endY = currentYear + 1; endY >= currentYear - 3; endY--) {
      const s = String((endY - 1) % 100).padStart(2, "0");
      const e = String(endY % 100).padStart(2, "0");
      list.push(`${s}/${e}`);
    }
    return list;
  }, [currentYear]);

  return (
    <div className="overview-page">
      <Topbar
        title="Dashboard"
        userName={user.username}
        onLogout={() => navigate("/")}
        user={user}
        sites={sites}
      />

      <main className="overview-main">
        {/* ── Toolbar ─────────────────────────────────────────────── */}
        <div className="ov-toolbar">
          <div>
            <h2 className="ov-heading">Vue d'ensemble</h2>
            <p className="ov-subheading">Indicateurs clés de performance — Campagne {selectedCampaign}</p>
          </div>
          <select
            className="ov-year-select"
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
          >
            {availableCampaigns.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* ── KPI Summary Row ──────────────────────────────────────── */}
        <div className="ov-kpi-row">
          <div
            className="ov-kpi-card ov-kpi-carbon"
            onClick={() => navigate("/carbon")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/carbon")}
          >
            <div className="ov-kpi-icon">
              <Leaf size={26} />
            </div>
            <div className="ov-kpi-content">
              <div className="ov-kpi-module">Bilan Carbone</div>
              <div className="ov-kpi-label">Émissions totales</div>
              {carbonLoading ? (
                <div className="ov-loading-inline">
                  <Loader2 size={18} className="ov-spin" />
                </div>
              ) : (
                <div className="ov-kpi-value">
                  {formatCO2(totalCO2)}
                  <span className="ov-kpi-unit"> kg eq.CO₂</span>
                </div>
              )}
            </div>
            <ArrowRight size={16} className="ov-kpi-arrow" />
          </div>

          <div
            className="ov-kpi-card ov-kpi-energy"
            onClick={() => navigate("/data-analysis/energie")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/data-analysis/energie")}
          >
            <div className="ov-kpi-icon">
              <Zap size={26} />
            </div>
            <div className="ov-kpi-content">
              <div className="ov-kpi-module">Énergie</div>
              <div className="ov-kpi-label">Consommation totale</div>
              {energieKpis.isLoading ? (
                <div className="ov-loading-inline">
                  <Loader2 size={18} className="ov-spin" />
                </div>
              ) : (
                <div className="ov-kpi-value">
                  {formatNum(energieTEP)}
                  <span className="ov-kpi-unit"> TEP</span>
                </div>
              )}
            </div>
            <ArrowRight size={16} className="ov-kpi-arrow" />
          </div>

          <div
            className="ov-kpi-card ov-kpi-water"
            onClick={() => navigate("/data-analysis/eau")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/data-analysis/eau")}
          >
            <div className="ov-kpi-icon">
              <Droplets size={26} />
            </div>
            <div className="ov-kpi-content">
              <div className="ov-kpi-module">Eau</div>
              <div className="ov-kpi-label">Consommation totale</div>
              {eauKpis.isLoading ? (
                <div className="ov-loading-inline">
                  <Loader2 size={18} className="ov-spin" />
                </div>
              ) : (
                <div className="ov-kpi-value">
                  {formatNum(eauTotal, 0)}
                  <span className="ov-kpi-unit"> m³</span>
                </div>
              )}
            </div>
            <ArrowRight size={16} className="ov-kpi-arrow" />
          </div>
        </div>

        {/* ── Bilan Carbone (featured) ─────────────────────────────── */}
        <div className="ov-section ov-section-featured">
          <div className="ov-section-header">
            <div className="ov-section-title-row">
              <Leaf size={18} className="ov-icon-carbon" />
              <span className="ov-section-title">Bilan Carbone</span>
              <span className="ov-badge-featured">Prioritaire</span>
            </div>
            <button className="ov-btn-detail" onClick={() => navigate("/carbon")}>
              Voir le détail <ArrowRight size={13} />
            </button>
          </div>

          {carbonLoading ? (
            <div className="ov-loading-block">
              <Loader2 size={36} className="ov-spin" />
            </div>
          ) : totalCO2 === 0 ? (
            <div className="ov-empty">Aucune donnée disponible pour la campagne {selectedCampaign}</div>
          ) : (
            <div className="ov-carbon-content">
              <div className="ov-donut-wrap">
                <Doughnut data={donutData} options={donutOptions} />
                <div className="ov-donut-center">
                  <span className="ov-donut-total">{formatCO2(totalCO2)}</span>
                  <span className="ov-donut-unit">kg CO₂</span>
                </div>
              </div>
              <div className="ov-postes-list">
                {POSTE_LABELS.map((label, i) => {
                  if (posteTotals[i] <= 0) return null;
                  const pct = totalCO2 > 0 ? (posteTotals[i] / totalCO2) * 100 : 0;
                  return (
                    <div key={label} className="ov-poste-row">
                      <span className="ov-poste-dot" style={{ background: POSTE_COLORS[i] }} />
                      <span className="ov-poste-name">{label}</span>
                      <div className="ov-poste-bar">
                        <div
                          className="ov-poste-bar-fill"
                          style={{ width: `${pct}%`, background: POSTE_COLORS[i] }}
                        />
                      </div>
                      <span className="ov-poste-val">{formatCO2(posteTotals[i])}</span>
                      <span className="ov-poste-pct">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Energie + Eau row ────────────────────────────────────── */}
        <div className="ov-modules-row">
          {/* Energie */}
          <div className="ov-section">
            <div className="ov-section-header">
              <div className="ov-section-title-row">
                <Zap size={18} className="ov-icon-energy" />
                <span className="ov-section-title">Énergie</span>
              </div>
              <button
                className="ov-btn-detail"
                onClick={() => navigate("/data-analysis/energie")}
              >
                Voir le détail <ArrowRight size={13} />
              </button>
            </div>

            {energieKpis.isLoading ? (
              <div className="ov-loading-block">
                <Loader2 size={28} className="ov-spin" />
              </div>
            ) : !energieKpis.data?.summary?.length ? (
              <div className="ov-empty">Aucune donnée disponible</div>
            ) : (
              <div className="ov-bars-list">
                {(() => {
                  const maxVal = Math.max(
                    ...energieKpis.data!.summary.map((x) => x.total),
                    1
                  );
                  return energieKpis.data!.summary.map((item, i) => {
                    const label = item.code
                      .replace("ENERGIE_", "")
                      .replace("_TEP", "");
                    return (
                      <div key={item.code} className="ov-bar-row">
                        <span className="ov-bar-label">{label}</span>
                        <div className="ov-bar-track">
                          <div
                            className="ov-bar-fill"
                            style={{
                              width: `${(item.total / maxVal) * 100}%`,
                              background:
                                ENERGIE_BAR_COLORS[i % ENERGIE_BAR_COLORS.length],
                            }}
                          />
                        </div>
                        <span className="ov-bar-value">
                          {formatNum(item.total)} TEP
                        </span>
                      </div>
                    );
                  });
                })()}
                <div className="ov-module-total">
                  Total : <strong>{formatNum(energieTEP)} TEP</strong>
                </div>
              </div>
            )}
          </div>

          {/* Eau */}
          <div className="ov-section">
            <div className="ov-section-header">
              <div className="ov-section-title-row">
                <Droplets size={18} className="ov-icon-water" />
                <span className="ov-section-title">Eau</span>
              </div>
              <button
                className="ov-btn-detail"
                onClick={() => navigate("/data-analysis/eau")}
              >
                Voir le détail <ArrowRight size={13} />
              </button>
            </div>

            {eauKpis.isLoading ? (
              <div className="ov-loading-block">
                <Loader2 size={28} className="ov-spin" />
              </div>
            ) : !eauKpis.data?.summary?.length ? (
              <div className="ov-empty">Aucune donnée disponible</div>
            ) : (
              <div className="ov-bars-list">
                {(() => {
                  const maxVal = Math.max(
                    ...eauKpis.data!.summary.map((x) => x.total),
                    1
                  );
                  return eauKpis.data!.summary.map((item, i) => (
                    <div key={item.code} className="ov-bar-row">
                      <span className="ov-bar-label">{item.name}</span>
                      <div className="ov-bar-track">
                        <div
                          className="ov-bar-fill"
                          style={{
                            width: `${(item.total / maxVal) * 100}%`,
                            background:
                              EAU_BAR_COLORS[i % EAU_BAR_COLORS.length],
                          }}
                        />
                      </div>
                      <span className="ov-bar-value">
                        {formatNum(item.total, 1)} {item.unit}
                      </span>
                    </div>
                  ));
                })()}
                <div className="ov-module-total">
                  Total : <strong>{formatNum(eauTotal, 1)} m³</strong>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardOverviewPage;
